import Vue, { VNode } from 'vue'
import toUpper from 'lodash/toUpper'
import { coreUtils } from '@ustra/core'
import { ConfigOption } from '../vue/components/option/component-option'

const DEFAULT_COMPONENT_OPTION: ConfigOption = {
  auth: {
    required: false,
    roles: [],
  },
}

/**
 * 특정 tag의 node를 찾는다.
 * @param nodes 조회 대상 root node
 * @param tagNames tag 명
 */
export function findVNodesByTag(nodes: VNode | VNode[], tagNames: string | string[]): VNode[] {
  const startNodes = Array.isArray(nodes) ? nodes : [nodes]

  const findFn = (nodes: VNode[], tagNames: string | string[]) => {
    let foundNodes: VNode[] = []
    const targetTagNames = typeof tagNames === 'string' ? [tagNames] : tagNames
    for (const node of nodes) {
      if (node.tag) {
        if (targetTagNames.includes(node.tag) || (node.componentOptions && targetTagNames.indexOf(node.componentOptions.tag)) > -1) {
          foundNodes.push(node)
        }
      }

      if (node.componentOptions) {
        if (node.componentOptions.children) {
          foundNodes = foundNodes.concat(findFn(node.componentOptions.children, tagNames))
        }
      }
    }

    return foundNodes
  }

  return findFn(startNodes, tagNames)
}

/**
 * 특정 tag의 node를 찾는다.
 * @param nodes 조회 대상 root node
 * @param tagNames tag 명 (없을 경우, 전체 조회)
 */
export function findComponentsByTag(components: Vue | Vue[], tagNames: string | string[] = []): Vue[] {
  const startComponents = Array.isArray(components) ? components : [components]

  const findFn = (components: Vue[], tagNames: string | string[]) => {
    let foundComponents: Vue[] = []
    const targetTagNames = (typeof tagNames === 'string' ? [tagNames] : tagNames).map(tn => toUpper(tn))

    for (const component of components) {
      if (!component) {
        continue
      }

      const tags = []
      if (component.$el) {
        tags.push(toUpper(component.$el.tagName))
      }

      if (component.$vnode && component.$vnode.componentOptions) {
        tags.push(toUpper(component.$vnode.componentOptions.tag))
      }

      if (targetTagNames.length === 0 || targetTagNames.some(tn => tags.some(tag => tag === tn))) {
        foundComponents.push(component)
      }

      if (component.$children) {
        foundComponents = foundComponents.concat(findFn([...component.$children], targetTagNames))
      }
    }

    return foundComponents
  }

  return findFn(startComponents, tagNames)
}

/**
 * 특정 tag의 Parent component를 찾는다.
 * @param nodes 조회 대상 root node
 * @param tagNames tag 명
 */
export function findParentComponents(nodes: Vue | Vue[], tagNames: string | string[]): Vue[] {
  const startNodes = Array.isArray(nodes) ? nodes : [nodes]

  const findFn = (components: Vue[], tagNames: string | string[]) => {
    let foundComponents: Vue[] = []
    const targetTagNames = (typeof tagNames === 'string' ? [tagNames] : tagNames).map(tn => toUpper(tn))

    for (const component of components) {
      if (!component) {
        continue
      }

      const tags = []
      if (component.$el) {
        tags.push(toUpper(component.$el.tagName))
      }

      if (component.$vnode && component.$vnode.componentOptions) {
        tags.push(toUpper(component.$vnode.componentOptions.tag))
      }

      if (targetTagNames.some(tn => tags.some(tag => tag === tn))) {
        foundComponents.push(component)
      }

      if (component.$parent) {
        foundComponents = foundComponents.concat(findFn([component.$parent], targetTagNames))
      }
    }

    return foundComponents
  }

  return findFn(startNodes, tagNames)
}

/**
 * 컴포넌트가 page 유입 컴포넌트인지 확인한다.
 * @param vm
 */
export const isPageComponent = (vm: Vue) => {
  if (vm.$route.matched && vm.$route.matched.length > 0) {
    for (const mc of vm.$route.matched) {
      const component = mc.instances?.default
      if (component === vm) {
        return true
      }

      const glComponent = mc.components?.default
      if (glComponent === vm) {
        return true
      }
    }

    return false
  }
}

/**
 * element로 부터 컴포넌트를 조회한다.
 * @param el element
 * @param findParent 상위 element 조회 여부
 */
export const getComponentFromElement = (el: Element, findParent: boolean = false) => {
  let currentElement = el

  while (currentElement.parentElement) {
    // @ts-ignore
    const component = currentElement.getAttribute('__vue__') || currentElement['__vue__']

    if (component) {
      return component
    }

    currentElement = currentElement.parentElement

    if (!findParent) {
      return null
    }
  }
}

/**
 * Ref 객체를 조회한다.
 * @param ref 조회할 ref 명
 */
export const getRef = <T = any>(vm: Vue, refName: string) => {
  const getRefInstance = async (refName: string) => {
    await vm.$nextTick()
    if (vm.$refs[refName]) {
      return vm.$refs[refName]
    } else {
      await coreUtils.sleep(5)
      return getRefInstance(refName)
    }
  }

  return new Promise<T>(resolve => {
    setTimeout(() => resolve(null), 3000)
    resolve(getRefInstance(refName))
  })
}

/**
 * 컴포넌트의 태그 명 조회
 * @param vm component instance
 */
export const getComponentTagName = (vm: Vue) => {
  // @ts-ignore
  return vm.$options._componentTag
}

/**
 * component 옵션을 조회한다.
 * @param vm vue 객체
 * @param defaultOption default option
 */
export const getComponentOption = (vm: Vue, defaultOption?: any) => {
  return coreUtils.deepMerge({}, DEFAULT_COMPONENT_OPTION, defaultOption || {}, vm.$options.config)
}

/**
 * 컴포넌트의 기본 옵션을 변경한다.
 * @param vueComponent Component type 객체
 * @param defaultOption 기본 옵션
 */
export const registerComponentDefaultOption = (vueComponent: typeof Vue, defaultOption: object) => {
  if (!defaultOption) {
    return
  }
  // @ts-ignore
  vueComponent.options.config = vueComponent.options.config || {}

  // @ts-ignore
  vueComponent.options.config.defaultOption = vueComponent.options.config.defaultOption || {}

  // @ts-ignore
  vueComponent.options.config.defaultOption = coreUtils.deepMerge({}, vueComponent.options.config.defaultOption, defaultOption)
}

/**
 * 부모 컴포넌트의 컴포넌트를 변경한다.
 * @param parentComponent 부모 컴포넌트
 * @param targetComponent 변경 대상 컴포넌트
 * @param name 컴포넌트 명
 */
export const replaceSubComponent = (parentComponent: any, targetComponent: any, name?: string) => {
  // @ts-ignore
  if (!targetComponent.options.name) {
    throw Error('target component required name property')
  }
  // @ts-ignore
  parentComponent.options.components = parentComponent.options.components || {}

  if (!name) {
    name = targetComponent.options.name
  }

  // @ts-ignore
  parentComponent.options.components[targetComponent.options.name] = targetComponent
}

